import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import JobFitColumn from "./JobFitColumn";
import styles from "./JobFitCheck.module.css";
import Spinner from "../career-companion/Spinner";

const CircularProgress = ({ value, size = 90, strokeWidth = 10 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (value / 100) * circumference;

    return (
        <div style={{ position: "relative", width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
                <circle cx={size / 2} cy={size / 2} r={radius} stroke="#E6E6E6" strokeWidth={strokeWidth} fill="none" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="#432C5B"
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 0.8s ease-out" }}
                />
            </svg>
            <div style={{ position: "absolute", fontSize: "18px", fontWeight: 700, color: "#432C5B" }}>
                {value ?? 0}
            </div>
        </div>
    );
};

function fmtDate(val) {
    if (!val) return "";
    try {
        return new Date(val).toLocaleString();
    } catch {
        return String(val);
    }
}

export default function JobFitCheckPage() {
    const { jobId } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showRaw, setShowRaw] = useState(false);


    const API_URL = process.env.REACT_APP_JOB_FIT_API_URL || "http://localhost:8510";

    useEffect(() => {
        const token = Cookies.get("token");
        if (!token) {
            navigate("/login", { replace: true });
            return;
        }

        // Check for cached data first (using localStorage for larger data)
        const cacheKey = `job_fit_${jobId}`;

        try {
            const cachedData = localStorage.getItem(cacheKey);

            if (cachedData) {
                const parsed = JSON.parse(cachedData);
                const cacheTimestamp = parsed._cacheTimestamp;
                const now = Date.now();
                const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

                // Check if cache is still valid (less than 7 days old)
                if (cacheTimestamp && (now - cacheTimestamp) < sevenDaysInMs) {
                    console.log("✅ Using cached job fit data for job:", jobId);
                    setData(parsed.data);
                    setError("");
                    setLoading(false);
                    return; // Skip backend call
                } else {
                    console.log("⏰ Cache expired for job:", jobId);
                    localStorage.removeItem(cacheKey);
                }
            }
        } catch (e) {
            console.error("❌ Failed to parse cached job fit data:", e);
            localStorage.removeItem(cacheKey);
            // Continue to fetch from backend
        }

        console.log("🔄 Fetching job fit data for:", jobId);
        (async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_URL}/job-fit`, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({ job_id: jobId }),
                });

                if (!res.ok) {
                    throw new Error("Failed to load job fit check");
                }

                const jsonData = await res.json();
                setData(jsonData);
                setError("");

                // Cache the job fit data in localStorage (with timestamp)
                try {
                    const cacheData = {
                        data: jsonData,
                        _cacheTimestamp: Date.now()
                    };
                    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
                    console.log("💾 Cached job fit data for job:", jobId);
                } catch (storageError) {
                    console.error("❌ Failed to cache job fit data:", storageError);
                    // Continue even if caching fails
                }
            } catch (e) {
                console.error("❌ Fetch error:", e);
                setError(e.message || "Failed to load job fit check");
            } finally {
                setLoading(false);
            }
        })();
    }, [jobId, navigate, API_URL]);

    if (loading) return <Spinner size="large" message="Checking job fit..." />;
    if (error) return <div className={styles.errorBox}><strong>Error:</strong> {error}</div>;
    if (!data) return null;

    const jm = data.job_meta || {};

    const salary =
        (jm.salary_min || jm.salary_max)
            ? `${jm.salary_min || ""}-${jm.salary_max || ""} ${jm.currency || ""}`.trim()
            : "Not provided";

    return (
        <div className={styles.page}>
            <div className={styles.pageInner}>
                <div className={styles.topBar}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>

                    <div className={styles.scoreWrap}>
                        <div className={styles.scoreText}>
                            <div className={styles.scoreLabel}>Job Fit</div>
                            <div className={styles.scoreMeta}>
                                Missing: {data.missing_items?.length || 0} • Matched: {data.matched_items?.length || 0}
                            </div>
                        </div>
                        <CircularProgress value={data.match_score || 0} />
                    </div>
                </div>



                <div className={styles.grid}>
                    <JobFitColumn
                        title={data.job_view?.title || "Job Ad"}
                        subtitle={[data.job_view?.company_name, data.job_view?.location].filter(Boolean).join(" • ")}
                        sections={data.job_view?.sections || []}
                    />

                    <JobFitColumn
                        title="CV"
                        subtitle={data.cv_view?.candidate_name || "Your CV"}
                        sections={data.cv_view?.sections || []}
                    />
                </div>
            </div>
        </div>
    );
}
